export const WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE = 'workspace-context-upload' as const;
export const PAGE_CHANGE_SET_STATUS_ITEM_TYPE = 'page-change-set' as const;

export type WorkspaceContextUploadStatus = 'queued' | 'processing' | 'ready' | 'failed';
export type PageChangeSetStatus = 'staged' | 'applying' | 'completed' | 'partly_applied' | 'canceled' | 'failed';
export type ChatStatusItemType =
    | typeof WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE
    | typeof PAGE_CHANGE_SET_STATUS_ITEM_TYPE;

export type ChatStatusItemBase<TType extends ChatStatusItemType, TStatus extends string> = {
    id: string;
    type: TType;
    status: TStatus;
    updatedAt: number;
};

export type ChatStatusItemEventBase<TType extends ChatStatusItemType> = {
    id: string;
    type: TType;
};

export type WorkspaceContextUploadStatusItem = ChatStatusItemBase<
    typeof WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE,
    WorkspaceContextUploadStatus
> & {
    contextFileId: string;
    filename: string;
    title: string;
    userVisibleError: string;
    userVisibleNotice: string;
};

export type WorkspaceContextUploadStatusItemEvent = (
    | WorkspaceContextUploadStatusItemUpsertEvent
    | WorkspaceContextUploadStatusItemRemoveEvent
);

type WorkspaceContextUploadStatusItemUpsertEvent = ChatStatusItemEventBase<
    typeof WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE
> & {
    contextFileId?: string;
    filename?: string;
    title?: string;
    status: WorkspaceContextUploadStatus;
    userVisibleError?: string;
    userVisibleNotice?: string;
    removed?: false;
};

type WorkspaceContextUploadStatusItemRemoveEvent = ChatStatusItemEventBase<
    typeof WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE
> & {
    contextFileId?: string;
    removed: true;
};

/** One entry of an addition's content: an option, a question with its options, a column or a phase. */
export type PageChangeContentItem = {
    text: string;
    detail: string;
    correct: boolean;
    items: PageChangeContentItem[];
};

/** One line of what an edit changes. `kind` is title, text, added, changed, removed, moved or setting. */
export type PageChangeEditLine = {
    kind: string;
    /** The new value, or for a removed item the one it had. */
    text: string;
    before: string | null;
    /** The setting changed. */
    name: string | null;
    /** An answer option rather than a question, column or other item. */
    option: boolean;
};

/**
 * Where a placed addition or a move puts its widget, next to an anchor. `kind` is `column` (in the anchor's column),
 * `newColumn` (side by side, in a new column) or `newContainer` (in a new container), `side` is `before` or `after`.
 */
export type PageChangePlacement = {
    kind: string;
    side: string;
    /** What the user knows the anchor by, empty for a column. */
    anchor: string;
    anchorId: string;
};

/** A column of the page as it will look once a set is applied. Items are widget ids and change keys. */
export type PageLayoutColumn = {
    key: string;
    id: string | null;
    width: number;
    items: string[];
};

export type PageLayoutRow = {
    key: string;
    id: string | null;
    columns: PageLayoutColumn[];
};

/** A container of the page as it will look once a set is applied. One a change adds has no id. */
export type PageLayoutContainer = {
    key: string;
    id: string | null;
    width: string;
    rows: PageLayoutRow[];
};

export type PageChangeOperation = {
    id: string;
    op: string;
    widgetType: string;
    icon: string;
    title: string;
    status: string;
    reason: string | null;
    /** The widget it added or changed, once applied. */
    widgetId: string | null;
    /** An addition's full description as plain text. */
    description: string;
    items: PageChangeContentItem[];
    /** Title of the widget an added word cloud, summary or mind map reads. */
    source: string | null;
    placement: PageChangePlacement | null;
    /** Who sees a moved or placed widget in its new place, when that changes: participants, adminsOnly or hidden. */
    visibility: string | null;
    /** What a move takes along: `widget`, or `container` for the whole container the widget is in. */
    scope: string;
    /** The container a move of a whole container moves. */
    containerId: string | null;
    /** A layout change's new column widths, or new container width. */
    widths: number[] | null;
    containerWidth: string | null;
    /** The widget an edit, delete or move changes. */
    target: string | null;
    /** An edit's new values: `title` as plain text, `text` as cleaned HTML. */
    fields: Record<string, string>;
    /** Everything an edit changes, for the user to review. */
    changes: PageChangeEditLine[];
    /** Whether the widget an edit or delete names had participant input when the change was staged. */
    hadInput: boolean;
    /** Why a change waiting for approval would not be applied as staged, such as its widget changed since. */
    blocked: string | null;
    /** Whether the user has to tick the change before it is applied, because it touches participant input. */
    needsConfirmation: boolean;
    /** What a new page is for. */
    goal: string;
    /** The widgets planned for a new page, in page order. */
    pageWidgets: PageChangeNewPageWidget[];
    /** Where a new page goes in the menu. */
    location: PageChangeLocation | null;
    /** A new page once it is made. */
    newPage: PageChangeNewPage | null;
    /** Titles of the pages and folders a deleted folder sends to "Pages not in menu". */
    leavingMenu: string[];
    /** The folder a move leaves empty, which the menu then does not show. */
    emptiedFolder: string | null;
};

export type PageChangeNewPageWidget = {
    type: string;
    icon: string;
    title: string;
};

/**
 * A new page or folder without a place goes to the `menuEnd`. Sets staged before that put a new page `next`, right
 * after the page the set is staged on. A moved page or folder goes `before` or `after` another one, or in a `folder`.
 */
export type PageChangeLocation = {
    kind: 'next' | 'after' | 'before' | 'folder' | 'newFolder' | 'menuEnd';
    title: string;
    /** The page or folder a new folder goes right after, empty for the page the set is staged on. */
    after: string;
};

export type PageChangeNewPageBuild = 'queued' | 'building' | 'done' | 'failed';

export type PageChangeNewPage = {
    id: string;
    path: string;
    build: PageChangeNewPageBuild;
    /** Whether participants can open it. */
    open: boolean;
    /** Who can open it: `participants`, `adminsOnly`, or `selected` users and lists. */
    access: string;
};

/** How many of the changes the user approved have an outcome, while the set is being applied. */
export type PageChangeSetProgress = {
    done: number;
    total: number;
};

/** Changes staged on one page, waiting for the user to apply or cancel them, or the outcome once they did. */
export type PageChangeSetStatusItem = ChatStatusItemBase<
    typeof PAGE_CHANGE_SET_STATUS_ITEM_TYPE,
    PageChangeSetStatus
> & {
    revision: number;
    /** Moves on with every write to the set, so an older copy of it is never shown over a newer one. */
    version: number;
    pageId: string;
    pageTitle: string;
    operations: PageChangeOperation[];
    /** The page as it will look once the set is applied, while it adds, moves or arranges widgets. */
    layout: PageLayoutContainer[] | null;
    progress: PageChangeSetProgress | null;
};

export type PageChangeSetStatusItemEvent = ChatStatusItemEventBase<typeof PAGE_CHANGE_SET_STATUS_ITEM_TYPE> & {
    status: PageChangeSetStatus;
    revision: number;
    version: number;
    pageId: string;
    pageTitle: string;
    operations: PageChangeOperation[];
    layout: PageLayoutContainer[] | null;
    progress: PageChangeSetProgress | null;
    removed?: false;
};

export type ChatStatusItem =
    | WorkspaceContextUploadStatusItem
    | PageChangeSetStatusItem;

export type ChatStatusItemEvent =
    | WorkspaceContextUploadStatusItemEvent
    | PageChangeSetStatusItemEvent;

export function chatStatusItemEventFromUnknown(value: unknown): ChatStatusItemEvent | null {
    if (!isRecord(value)) {
        return null;
    }

    switch (value.type) {
        case WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE:
            return workspaceContextUploadStatusItemEventFromRecord(value);
        case PAGE_CHANGE_SET_STATUS_ITEM_TYPE:
            return pageChangeSetStatusItemEventFromRecord(value);
        default:
            return null;
    }
}

export function chatStatusItemFromEvent(event: ChatStatusItemEvent, now: number): ChatStatusItem | null {
    if (chatStatusItemEventRemovesItem(event)) {
        return null;
    }

    switch (event.type) {
        case WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE:
            return workspaceContextUploadStatusItemFromEvent(event, now);
        case PAGE_CHANGE_SET_STATUS_ITEM_TYPE:
            return {...event, updatedAt: now};
        default:
            return null;
    }
}

export function chatStatusItemIdFromEvent(event: ChatStatusItemEvent): string {
    switch (event.type) {
        case WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE:
            return stringValue(event.id) || stringValue(event.contextFileId);
        case PAGE_CHANGE_SET_STATUS_ITEM_TYPE:
            return stringValue(event.id);
        default:
            return '';
    }
}

export function chatStatusItemEventRemovesItem(
    event: ChatStatusItemEvent,
): event is Extract<ChatStatusItemEvent, {removed: true}> {
    return event.removed === true;
}

export function chatStatusItemsEqual(left: ChatStatusItem, right: ChatStatusItem): boolean {
    return left.id === right.id
        && left.type === right.type
        && left.status === right.status
        && statusItemSpecificFingerprint(left) === statusItemSpecificFingerprint(right);
}

export function isWorkspaceContextUploadStatusItem(
    item: ChatStatusItem,
): item is WorkspaceContextUploadStatusItem {
    return item.type === WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE;
}

/**
 * Whether an incoming item is older than the one already shown. HTTP answers and websocket pushes for a change set
 * can arrive in either order.
 */
export function isOutdatedStatusItem(existing: ChatStatusItem, incoming: ChatStatusItem): boolean {
    if (!isPageChangeSetStatusItem(existing) || !isPageChangeSetStatusItem(incoming)) {
        return false;
    }

    return incoming.version < existing.version;
}

export function isPageChangeSetStatusItem(item: ChatStatusItem): item is PageChangeSetStatusItem {
    return item.type === PAGE_CHANGE_SET_STATUS_ITEM_TYPE;
}

export function workspaceContextUploadStatus(value: unknown): WorkspaceContextUploadStatus | null {
    return value === 'queued' || value === 'processing' || value === 'ready' || value === 'failed'
        ? value
        : null;
}

function workspaceContextUploadStatusItemEventFromRecord(
    record: Record<string, unknown>,
): WorkspaceContextUploadStatusItemEvent | null {
    if (record.removed === true) {
        return {
            type: WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE,
            id: stringValue(record.id),
            contextFileId: stringValue(record.contextFileId),
            removed: true,
        };
    }

    const status = workspaceContextUploadStatus(record.status);
    if (status === null) {
        return null;
    }

    return {
        type: WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE,
        id: stringValue(record.id),
        contextFileId: stringValue(record.contextFileId),
        filename: stringValue(record.filename),
        title: stringValue(record.title),
        status,
        userVisibleError: stringValue(record.userVisibleError),
        userVisibleNotice: stringValue(record.userVisibleNotice),
    };
}

function pageChangeSetStatusItemEventFromRecord(record: Record<string, unknown>): PageChangeSetStatusItemEvent | null {
    const status = record.status;
    if (
        status !== 'staged' && status !== 'applying' && status !== 'completed'
        && status !== 'partly_applied' && status !== 'canceled' && status !== 'failed'
    ) {
        return null;
    }

    return {
        type: PAGE_CHANGE_SET_STATUS_ITEM_TYPE,
        id: stringValue(record.id),
        status,
        revision: typeof record.revision === 'number' ? record.revision : 0,
        version: typeof record.version === 'number' ? record.version : 0,
        pageId: stringValue(record.pageId),
        pageTitle: stringValue(record.pageTitle),
        operations: Array.isArray(record.operations)
            ? record.operations.filter(isRecord).map(pageChangeOperationFromRecord)
            : [],
        layout: Array.isArray(record.layout) ? record.layout.filter(isRecord).map(layoutContainerFrom) : null,
        progress: isRecord(record.progress)
            && typeof record.progress.done === 'number'
            && typeof record.progress.total === 'number'
            ? {done: record.progress.done, total: record.progress.total}
            : null,
    };
}

function pageChangeOperationFromRecord(operation: Record<string, unknown>): PageChangeOperation {
    const placement = isRecord(operation.placement) ? operation.placement : null;
    const fields: Record<string, string> = {};
    if (isRecord(operation.fields)) {
        Object.entries(operation.fields).forEach(([name, value]) => {
            if (typeof value === 'string') {
                fields[name] = value;
            }
        });
    }

    return {
        id: stringValue(operation.id),
        op: stringValue(operation.op),
        widgetType: stringValue(operation.widgetType),
        icon: stringValue(operation.icon),
        title: stringValue(operation.title),
        status: stringValue(operation.status),
        reason: stringValue(operation.reason) || null,
        widgetId: stringValue(operation.widgetId) || null,
        description: stringValue(operation.description),
        items: contentItemsFrom(operation.items),
        source: stringValue(operation.source) || null,
        placement: placement === null
            ? null
            : {
                kind: stringValue(placement.kind),
                side: stringValue(placement.side),
                anchor: stringValue(placement.anchor),
                anchorId: stringValue(placement.anchorId),
            },
        visibility: stringValue(operation.visibility) || null,
        scope: stringValue(operation.scope) || 'widget',
        containerId: stringValue(operation.containerId) || null,
        widths: Array.isArray(operation.widths)
            ? operation.widths.filter((width): width is number => typeof width === 'number')
            : null,
        containerWidth: stringValue(operation.containerWidth) || null,
        target: stringValue(operation.target) || null,
        fields,
        changes: Array.isArray(operation.changes)
            ? operation.changes.filter(isRecord).map(line => ({
                kind: stringValue(line.kind),
                text: stringValue(line.text),
                before: typeof line.before === 'string' ? line.before : null,
                name: stringValue(line.name) || null,
                option: line.option === true,
            }))
            : [],
        hadInput: operation.hadInput === true,
        blocked: stringValue(operation.blocked) || null,
        needsConfirmation: operation.needsConfirmation === true,
        goal: stringValue(operation.goal),
        pageWidgets: Array.isArray(operation.pageWidgets)
            ? operation.pageWidgets.filter(isRecord).map(widget => ({
                type: stringValue(widget.type),
                icon: stringValue(widget.icon),
                title: stringValue(widget.title),
            }))
            : [],
        location: locationFrom(operation.location),
        newPage: newPageFrom(operation.newPage),
        leavingMenu: Array.isArray(operation.leavingMenu)
            ? operation.leavingMenu.map(stringValue).filter(title => title !== '')
            : [],
        emptiedFolder: stringValue(operation.emptiedFolder) || null,
    };
}

function layoutContainerFrom(container: Record<string, unknown>): PageLayoutContainer {
    return {
        key: stringValue(container.key),
        id: stringValue(container.id) || null,
        width: stringValue(container.width),
        rows: (Array.isArray(container.rows) ? container.rows.filter(isRecord) : []).map(row => ({
            key: stringValue(row.key),
            id: stringValue(row.id) || null,
            columns: (Array.isArray(row.columns) ? row.columns.filter(isRecord) : []).map(column => ({
                key: stringValue(column.key),
                id: stringValue(column.id) || null,
                width: typeof column.width === 'number' ? column.width : 100,
                items: Array.isArray(column.items) ? column.items.map(stringValue).filter(item => item !== '') : [],
            })),
        })),
    };
}

/**
 * The labels that say why a staged change no longer holds, most specific first: a change to a page or folder has its
 * own wording where the shared one speaks of a widget.
 */
export function blockedLabelKeys(operation: PageChangeOperation): string[] {
    const reason = operation.blocked;
    if (reason === null) {
        return [];
    }
    const own = operation.widgetType === 'page' || operation.widgetType === 'folder'
        ? [`blocked_${reason}_${operation.widgetType}`]
        : [];

    return [...own, `blocked_${reason}`, `reason_${reason}`];
}

/** A change to the menu rather than to a page: a new folder, a move of a page or folder, or a folder delete. */
export function isMenuChange(operation: PageChangeOperation): boolean {
    return operation.op === 'move_page' || operation.op === 'add_folder' || operation.widgetType === 'folder';
}

function locationFrom(value: unknown): PageChangeLocation | null {
    if (!isRecord(value)) {
        return null;
    }
    const kind = value.kind;
    if (
        kind !== 'next'
        && kind !== 'after'
        && kind !== 'before'
        && kind !== 'folder'
        && kind !== 'newFolder'
        && kind !== 'menuEnd'
    ) {
        return null;
    }

    return {kind, title: stringValue(value.title), after: stringValue(value.after)};
}

function newPageFrom(value: unknown): PageChangeNewPage | null {
    if (!isRecord(value) || stringValue(value.id) === '') {
        return null;
    }
    const build = value.build;

    return {
        id: stringValue(value.id),
        path: stringValue(value.path),
        build: build === 'queued' || build === 'building' || build === 'done' ? build : 'failed',
        open: value.open === true,
        access: stringValue(value.access),
    };
}

function contentItemsFrom(value: unknown): PageChangeContentItem[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.filter(isRecord).map(item => ({
        text: stringValue(item.text),
        detail: stringValue(item.detail),
        correct: item.correct === true,
        items: contentItemsFrom(item.items),
    })).filter(item => item.text !== '');
}

function workspaceContextUploadStatusItemFromEvent(
    event: WorkspaceContextUploadStatusItemUpsertEvent,
    now: number,
): WorkspaceContextUploadStatusItem | null {
    const id = chatStatusItemIdFromEvent(event);
    const status = workspaceContextUploadStatus(event.status);
    if (id === '' || status === null) {
        return null;
    }

    return {
        id,
        type: WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE,
        contextFileId: stringValue(event.contextFileId) || id,
        status,
        filename: stringValue(event.filename),
        title: stringValue(event.title),
        userVisibleError: stringValue(event.userVisibleError),
        userVisibleNotice: stringValue(event.userVisibleNotice),
        updatedAt: now,
    };
}

function statusItemSpecificFingerprint(item: ChatStatusItem): string {
    switch (item.type) {
        case WORKSPACE_CONTEXT_UPLOAD_STATUS_ITEM_TYPE:
            return [
                item.contextFileId,
                item.filename,
                item.title,
                item.userVisibleError,
                item.userVisibleNotice,
            ].join('\u0001');
        case PAGE_CHANGE_SET_STATUS_ITEM_TYPE:
            return [item.version, item.pageTitle, JSON.stringify(item.operations)].join('\u0001');
        default:
            return '';
    }
}

function stringValue(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
